import React, { useEffect, useMemo, useState } from 'react';
import { Document, Page, pdfjs } from 'react-pdf';
import { ChevronLeft, ChevronRight, X, FileText, Image as ImageIcon, Download } from 'lucide-react';
import 'react-pdf/dist/Page/AnnotationLayer.css';
import 'react-pdf/dist/Page/TextLayer.css';

// PDF.js worker — pinned to the version bundled with the installed
// pdfjs-dist. The .mjs URL is the only path the worker accepts on
// pdfjs-dist >= 4.
pdfjs.GlobalWorkerOptions.workerSrc = new URL(
  'pdfjs-dist/build/pdf.worker.min.mjs',
  import.meta.url,
).toString();

/**
 * FilePreviewCarousel — slide-through modal that renders the files the
 * user has just selected. PDFs go through react-pdf (PDF.js), images
 * use plain <img>, anything else falls back to a "no preview" tile.
 *
 * Props:
 *   files: File[]                — user-picked File objects
 *   initialIndex: number         — start index (default 0)
 *   open: boolean
 *   onClose: () => void
 *   tr: boolean                  — Turkish label flag
 */
export default function FilePreviewCarousel({ files = [], initialIndex = 0, open, onClose, tr = true }) {
  const [idx, setIdx] = useState(initialIndex);
  // PDF page state — keyed by file index so each PDF starts at page 1.
  // We store the current page per-idx in a Map ref-style state; rendering
  // only consults `pdfPage[idx] || 1` so changing `idx` automatically
  // resets to "page 1" without firing an effect.
  const [pdfPage, setPdfPage] = useState({});  // { [idx]: pageNum }
  const [pdfTotal, setPdfTotal] = useState({}); // { [idx]: totalPages }

  const curPdfPage = pdfPage[idx] || 1;
  const curPdfTotal = pdfTotal[idx] || 0;

  // Build object URLs once per file and free them on unmount / file change.
  const urls = useMemo(
    () => files.map(f => ({ url: URL.createObjectURL(f), name: f.name, size: f.size, type: f.type })),
    [files],
  );
  useEffect(() => () => urls.forEach(u => URL.revokeObjectURL(u.url)), [urls]);

  // Keyboard nav
  useEffect(() => {
    if (!open) return;
    const onKey = (e) => {
      if (e.key === 'Escape') onClose?.();
      if (e.key === 'ArrowRight') setIdx(i => Math.min(i + 1, files.length - 1));
      if (e.key === 'ArrowLeft')  setIdx(i => Math.max(i - 1, 0));
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, files.length, onClose]);

  if (!open || files.length === 0) return null;

  const cur = urls[idx];
  const ext = (cur?.name || '').split('.').pop().toLowerCase();
  const isPdf = ext === 'pdf';
  const isImg = ['jpg', 'jpeg', 'png', 'webp', 'gif'].includes(ext);

  const next = () => setIdx(i => Math.min(i + 1, files.length - 1));
  const prev = () => setIdx(i => Math.max(i - 1, 0));

  return (
    <div
      data-testid="file-preview-carousel"
      className="fixed inset-0 z-[10001] bg-black/80 backdrop-blur-sm flex items-center justify-center px-4 py-6"
      onClick={(e) => { if (e.target === e.currentTarget) onClose?.(); }}
    >
      <div className="bg-white rounded-3xl shadow-2xl max-w-4xl w-full max-h-[92vh] flex flex-col overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100 bg-gradient-to-r from-orange-500 to-red-500">
          <div className="flex items-center gap-3 min-w-0">
            {isPdf
              ? <span className="inline-flex items-center justify-center w-8 h-8 rounded bg-white/20 text-white text-[10px] font-black flex-shrink-0">PDF</span>
              : isImg
                ? <ImageIcon className="h-5 w-5 text-white flex-shrink-0" />
                : <FileText className="h-5 w-5 text-white flex-shrink-0" />
            }
            <div className="min-w-0">
              <div className="text-white font-semibold text-sm truncate" title={cur?.name}>{cur?.name}</div>
              <div className="text-white/70 text-xs">
                {tr ? 'Dosya' : 'File'} {idx + 1} / {files.length}
                {cur?.size != null && (
                  <span className="ml-2">· {cur.size > 1024 * 1024 ? `${(cur.size / 1024 / 1024).toFixed(1)} MB` : `${(cur.size / 1024).toFixed(0)} KB`}</span>
                )}
                {isPdf && curPdfTotal > 0 && (
                  <span className="ml-2">· {tr ? 'Sayfa' : 'Page'} {curPdfPage}/{curPdfTotal}</span>
                )}
              </div>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <a
              data-testid="file-preview-download"
              href={cur?.url}
              download={cur?.name}
              className="h-9 w-9 rounded-full bg-white/15 hover:bg-white/25 text-white flex items-center justify-center transition-colors"
              title={tr ? 'İndir' : 'Download'}
            >
              <Download className="h-4 w-4" />
            </a>
            <button
              data-testid="file-preview-close"
              type="button"
              onClick={onClose}
              className="h-9 w-9 rounded-full bg-white/15 hover:bg-white/25 text-white flex items-center justify-center transition-colors"
              title={tr ? 'Kapat' : 'Close'}
            >
              <X className="h-5 w-5" />
            </button>
          </div>
        </div>

        {/* Body — file viewer */}
        <div className="relative flex-1 bg-gray-50 overflow-auto flex items-center justify-center">
          {/* Prev arrow */}
          {files.length > 1 && idx > 0 && (
            <button
              data-testid="file-preview-prev"
              type="button"
              onClick={prev}
              className="absolute left-3 top-1/2 -translate-y-1/2 z-10 h-11 w-11 rounded-full bg-white shadow-md text-gray-700 hover:bg-orange-50 hover:text-orange-600 flex items-center justify-center transition-all"
            >
              <ChevronLeft className="h-5 w-5" />
            </button>
          )}
          {/* Next arrow */}
          {files.length > 1 && idx < files.length - 1 && (
            <button
              data-testid="file-preview-next"
              type="button"
              onClick={next}
              className="absolute right-3 top-1/2 -translate-y-1/2 z-10 h-11 w-11 rounded-full bg-white shadow-md text-gray-700 hover:bg-orange-50 hover:text-orange-600 flex items-center justify-center transition-all"
            >
              <ChevronRight className="h-5 w-5" />
            </button>
          )}

          {/* Viewer */}
          <div className="w-full h-full min-h-[60vh] p-4 flex items-center justify-center">
            {isPdf ? (
              <div className="bg-white shadow-sm rounded-lg overflow-hidden">
                <Document
                  file={cur.url}
                  onLoadSuccess={({ numPages }) => setPdfTotal(t => ({ ...t, [idx]: numPages }))}
                  loading={<div className="px-10 py-16 text-sm text-gray-400">{tr ? 'PDF yükleniyor…' : 'Loading PDF…'}</div>}
                  error={<div className="px-10 py-16 text-sm text-red-500">{tr ? 'PDF gösterilemedi.' : 'Could not render PDF.'}</div>}
                >
                  <Page pageNumber={curPdfPage} width={Math.min(window.innerWidth - 120, 720)} />
                </Document>
                {curPdfTotal > 1 && (
                  <div className="flex items-center justify-center gap-3 py-2 border-t border-gray-100 bg-gray-50">
                    <button
                      type="button"
                      onClick={() => setPdfPage(p => ({ ...p, [idx]: Math.max(1, (p[idx] || 1) - 1) }))}
                      disabled={curPdfPage <= 1}
                      className="px-3 py-1 rounded-lg text-xs font-semibold bg-white border border-gray-200 disabled:opacity-40"
                    >
                      ‹ {tr ? 'Önceki' : 'Prev'}
                    </button>
                    <span className="text-xs text-gray-500">{curPdfPage} / {curPdfTotal}</span>
                    <button
                      type="button"
                      onClick={() => setPdfPage(p => ({ ...p, [idx]: Math.min(curPdfTotal, (p[idx] || 1) + 1) }))}
                      disabled={curPdfPage >= curPdfTotal}
                      className="px-3 py-1 rounded-lg text-xs font-semibold bg-white border border-gray-200 disabled:opacity-40"
                    >
                      {tr ? 'Sonraki' : 'Next'} ›
                    </button>
                  </div>
                )}
              </div>
            ) : isImg ? (
              <img
                src={cur.url}
                alt={cur.name}
                className="max-w-full max-h-[72vh] object-contain shadow-md rounded-lg bg-white"
              />
            ) : (
              <div className="text-center py-16 px-6 max-w-md">
                <FileText className="h-16 w-16 text-gray-300 mx-auto mb-4" />
                <p className="text-gray-500 text-sm">
                  {tr
                    ? 'Bu dosya türü için önizleme mevcut değil. İndir simgesinden indirebilirsiniz.'
                    : 'Preview is not available for this file type. Use the download icon.'}
                </p>
              </div>
            )}
          </div>
        </div>

        {/* Thumbnail strip */}
        {files.length > 1 && (
          <div className="border-t border-gray-100 bg-white px-4 py-3 flex items-center gap-2 overflow-x-auto">
            {urls.map((u, i) => {
              const e = u.name.split('.').pop().toLowerCase();
              const pdf = e === 'pdf';
              const img = ['jpg', 'jpeg', 'png', 'webp'].includes(e);
              const active = i === idx;
              return (
                <button
                  key={i}
                  type="button"
                  data-testid={`file-preview-thumb-${i}`}
                  onClick={() => setIdx(i)}
                  className={`flex-shrink-0 h-14 w-14 rounded-lg border-2 overflow-hidden flex items-center justify-center text-xs font-bold transition-all ${
                    active ? 'border-orange-500 ring-2 ring-orange-200' : 'border-gray-200 hover:border-orange-300'
                  }`}
                  title={u.name}
                >
                  {img
                    ? <img src={u.url} alt={u.name} className="h-full w-full object-cover" />
                    : pdf
                      ? <span className="text-red-600">PDF</span>
                      : <FileText className="h-5 w-5 text-gray-400" />
                  }
                </button>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
